import { useEffect } from 'react';

// Images that can't use the SmartImage wrapper get revealed here instead:
//  - article body images (injected via lazy <Content/>) → shimmer on the img
//  - stacked crossfade galleries → shimmer on the container, cleared once any
//    of its images decodes (the imgs are absolutely positioned, so wrapping
//    them would break the crossfade).
const IMG_SELECTOR =
  '.blog-detail__article img, .compare-stories__logos img, .compare-platforms__logo img';
const CONTAINER_SELECTOR =
  '.setup__media, .case-studies__media, .trust__item, .integrations__tile';

// `key` (e.g. pathname) re-runs the initial sweep on navigation.
export default function useImageReveal(key) {
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const mark = (el) => {
      if (el && !el.classList.contains('is-loaded')) el.classList.add('is-loaded');
    };

    const revealFrom = (img) => {
      if (!img || img.tagName !== 'IMG') return;
      if (img.matches(IMG_SELECTOR)) mark(img);
      const container = img.closest(CONTAINER_SELECTOR);
      if (container) mark(container);
    };

    // Capture phase: `load`/`error` don't bubble, so listen on the way down.
    const onLoad = (e) => revealFrom(e.target);

    document.addEventListener('load', onLoad, true);
    document.addEventListener('error', onLoad, true);

    // Sweep images already in the DOM (cached / pre-rendered).
    const sweep = () => {
      document.querySelectorAll(`${IMG_SELECTOR}, ${CONTAINER_SELECTOR} img`).forEach((img) => {
        if (img.complete && img.naturalWidth > 0) revealFrom(img);
      });
    };
    sweep();

    // Article and gallery content can mount after this hook; catch later images.
    // Debounce via rAF so frequent unrelated DOM changes (e.g. the hero
    // typewriter) don't trigger a querySelectorAll on every mutation.
    let scheduled = false;
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        sweep();
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      document.removeEventListener('load', onLoad, true);
      document.removeEventListener('error', onLoad, true);
      observer.disconnect();
    };
  }, [key]);
}
